import { useEffect, useState } from "react";
import api, { formatEUR, currentMonth, monthLabel } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Budgets() {
  const [month, setMonth] = useState(currentMonth());
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [txs, setTxs] = useState([]);
  const [open, setOpen] = useState(false);
  const [catId, setCatId] = useState("");
  const [amount, setAmount] = useState("");

  const load = async () => {
    const [b, c, t] = await Promise.all([
      api.get(`/budgets?month=${month}`),
      api.get("/categories"),
      api.get(`/transactions?month=${month}&type=expense`),
    ]);
    setBudgets(b.data);
    setCategories(c.data);
    setTxs(t.data);
  };
  useEffect(() => { load(); }, [month]);

  const expenseByCat = {};
  txs.forEach((t) => { expenseByCat[t.category_id] = (expenseByCat[t.category_id] || 0) + t.amount; });

  const submit = async () => {
    if (!catId || !amount) return toast.error("Complétez tous les champs");
    await api.post("/budgets", { category_id: catId, amount: parseFloat(amount), month });
    toast.success("Budget enregistré");
    setOpen(false); setAmount(""); setCatId("");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/budgets/${id}`);
    load();
  };

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const expenseCats = categories.filter((c) => c.type === "expense");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Budgets</h1>
          <p className="text-stone-500 mt-1 capitalize">{monthLabel(month)}</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="rounded-xl border border-stone-200 h-10 px-3 text-sm bg-white" data-testid="budget-month-filter" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="btn-add-budget">
                <Plus className="w-4 h-4 mr-1" /> Budget
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Nouveau budget</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Catégorie</Label>
                  <Select value={catId} onValueChange={setCatId}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="budget-cat-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      {expenseCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Montant mensuel (€)</Label>
                  <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                         className="rounded-xl h-11" data-testid="budget-amount-input" />
                </div>
                <Button onClick={submit} className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="budget-submit-button">
                  Enregistrer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {budgets.length === 0 ? (
        <Card className="p-12 rounded-2xl border-stone-200 text-center">
          <p className="text-stone-400">Aucun budget défini pour ce mois.</p>
          <p className="text-stone-500 text-sm mt-2">Créez votre premier budget pour suivre vos objectifs.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgets.map((b) => {
            const cat = catMap[b.category_id];
            const spent = expenseByCat[b.category_id] || 0;
            const pct = Math.min(100, (spent / b.amount) * 100);
            const over = spent > b.amount;
            return (
              <Card key={b.id} className="p-5 rounded-2xl border-stone-200" data-testid={`budget-${b.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                         style={{ backgroundColor: (cat?.color || "#78716C") + "20", color: cat?.color || "#78716C" }}>
                      <span className="text-xs font-bold">{cat?.name?.slice(0, 2).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="font-semibold">{cat?.name || "Catégorie"}</div>
                      <div className="text-xs text-stone-500">Budget mensuel</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(b.id)} data-testid={`budget-delete-${b.id}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-mono-num font-semibold text-lg">
                    <span className={over ? "text-rose-600" : "text-stone-900"}>{formatEUR(spent)}</span>
                    <span className="text-stone-400 text-sm"> / {formatEUR(b.amount)}</span>
                  </div>
                  <div className={`text-xs font-medium ${over ? "text-rose-600" : "text-stone-500"}`}>
                    {pct.toFixed(0)}%
                  </div>
                </div>
                <Progress value={pct} className={`h-2 ${over ? "[&>div]:bg-rose-500" : "[&>div]:bg-emerald-500"}`} />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
